import { ReaderSettings, Bookmark, BacklogEntry, DEFAULT_READER_SETTINGS } from '../types/reader';
import { TOTAL_PAGES } from '../types/tome';

export const SCHEMA_VERSION = 1;

export const STORAGE_KEYS = {
  SCHEMA_VERSION: 'caesura_schema_version',
  PAGE: 'caesura_page',
  SETTINGS: 'caesura_settings',
  BOOKMARKS: 'caesura_bookmarks',
  BACKLOG: 'caesura_backlog',
} as const;

export const DEFAULT_SETTINGS: ReaderSettings = { ...DEFAULT_READER_SETTINGS };

// Internal in-memory fallback for environments where LocalStorage throws or is unavailable
const memoryStorage = new Map<string, string>();

function isLocalStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    const testKey = '__caesura_storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function getItem(key: string): string | null {
  // Dual-tier synchronization: if item fell back to memory, return it directly
  if (memoryStorage.has(key)) {
    return memoryStorage.get(key) || null;
  }
  if (isLocalStorageAvailable()) {
    try {
      const val = window.localStorage.getItem(key);
      if (val !== null) {
        return val;
      }
    } catch {
      // Storage access threw (e.g. SecurityError in restricted iframe)
    }
  }
  return memoryStorage.get(key) || null;
}

function setItem(key: string, value: string): void {
  if (isLocalStorageAvailable()) {
    try {
      window.localStorage.setItem(key, value);
      // Clean up stale fallback memory storage if real write succeeded
      memoryStorage.delete(key);
      return;
    } catch {
      // QuotaExceededError or SecurityError fallback
      memoryStorage.set(key, value);
      return;
    }
  }
  memoryStorage.set(key, value);
}

function removeItem(key: string): void {
  if (isLocalStorageAvailable()) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore
    }
  }
  memoryStorage.delete(key);
}

// ----------------------------------------------------
// Public Storage API with Schema Migration & Clamping
// ----------------------------------------------------

export function initializeStorage(): void {
  try {
    const currentVersionStr = getItem(STORAGE_KEYS.SCHEMA_VERSION);
    const currentVersion = currentVersionStr ? parseInt(currentVersionStr, 10) : 0;

    if (currentVersion < SCHEMA_VERSION) {
      setItem(STORAGE_KEYS.SCHEMA_VERSION, SCHEMA_VERSION.toString());
    }
  } catch {
    // Ignore initialization failure
  }
}

export function saveCurrentPage(page: number): void {
  const clamped = Math.max(1, Math.min(TOTAL_PAGES, Math.floor(page || 1)));
  setItem(STORAGE_KEYS.PAGE, clamped.toString());
}

export function loadCurrentPage(): number {
  try {
    const raw = getItem(STORAGE_KEYS.PAGE);
    if (!raw) return 1;
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return 1;
    return Math.max(1, Math.min(TOTAL_PAGES, parsed));
  } catch {
    return 1;
  }
}

export function saveSettings(settings: ReaderSettings): void {
  try {
    setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch {
    // Silently continue
  }
}

export function loadSettings(): ReaderSettings {
  try {
    const raw = getItem(STORAGE_KEYS.SETTINGS);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveBookmarks(bookmarks: Bookmark[]): void {
  try {
    setItem(STORAGE_KEYS.BOOKMARKS, JSON.stringify(bookmarks));
  } catch {
    // Silently continue
  }
}

export function loadBookmarks(): Bookmark[] {
  try {
    const raw = getItem(STORAGE_KEYS.BOOKMARKS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (b) => typeof b === 'object' && b !== null && typeof b.pageNumber === 'number' && b.pageNumber >= 1 && b.pageNumber <= TOTAL_PAGES
    );
  } catch {
    return [];
  }
}

export function saveBacklog(backlog: BacklogEntry[]): void {
  try {
    // Cap backlog to max 150 entries to prevent storage bloat
    const capped = backlog.slice(0, 150);
    setItem(STORAGE_KEYS.BACKLOG, JSON.stringify(capped));
  } catch {
    // Silently continue
  }
}

export function loadBacklog(): BacklogEntry[] {
  try {
    const raw = getItem(STORAGE_KEYS.BACKLOG);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

export function clearAllStorage(): void {
  try {
    Object.values(STORAGE_KEYS).forEach((k) => removeItem(k));
  } catch {
    // Ignore
  }
  if (isLocalStorageAvailable()) {
    try {
      window.localStorage.clear();
    } catch {
      // Ignore
    }
  }
  memoryStorage.clear();
  setItem(STORAGE_KEYS.SCHEMA_VERSION, SCHEMA_VERSION.toString());
}
